/**
 * Client sidebar navigation config (#1323).
 *
 * Pure, React-free decision module: given the firm/pilot flags it decides
 * whether a firm-managed client gets the COMPACT workflow-first nav (≤6
 * primary items + a "More" group) or the existing FULL nav. The decision is
 * exhaustively unit-tested; Sidebar.tsx consumes the result and maps the
 * string `iconKey`s to lucide icon components.
 *
 * Compact nav shows for EVERY firm-managed client (#1516) — a client whose
 * case has an active advisor_connection must never see the legacy DIY nav on
 * any route. DIY / individual clients keep the full nav, so there is zero
 * regression for the existing self-serve users.
 */

import type { FeaturePreset } from '@/lib/features/registry';

/** Icon identifiers resolved to lucide components in Sidebar.tsx. */
export type NavIconKey =
  | 'home'
  | 'tasks'
  | 'disclosure'
  | 'documents'
  | 'discovery'
  | 'team'
  | 'financials'
  | 'strategy'
  | 'support'
  | 'export'
  | 'intelligence'
  | 'askSage'
  | 'notes'
  | 'help'
  | 'settings';

/** A single compact-nav item (decision-layer shape — no React). */
export interface ClientNavItem {
  /** Resolved, i18n'd label. */
  label: string;
  /** App route (must exist). */
  href: string;
  /** Icon identifier resolved by the renderer. */
  iconKey: NavIconKey;
}

/** Compact, workflow-first nav for firm-managed clients. */
export interface CompactClientNav {
  mode: 'compact';
  /** At most 6 primary items, workflow-first. */
  primary: ClientNavItem[];
  /** Everything else, behind a collapsible "More" group. */
  more: ClientNavItem[];
}

/** Full nav — Sidebar renders its existing sections unchanged. */
export interface FullClientNav {
  mode: 'full';
}

export type ClientNav = CompactClientNav | FullClientNav;

/** Hard cap on primary items in compact mode (contract: nav-item-primary ≤ 6). */
export const MAX_PRIMARY_ITEMS = 6;

export interface BuildClientNavParams {
  /**
   * HHD / firm pilot flag (PostHog `hhd-pilot`). No longer part of the nav
   * decision (#1516) — accepted so existing call sites/tests keep compiling;
   * Sidebar still uses it separately for progressive-mode phasing.
   */
  isHHDPilot?: boolean;
  /** Active advisor_connection exists for this client. */
  isFirmManaged: boolean;
  /** i18n translator (navigation namespace). */
  t: (key: string, fallback?: string) => string;
  /**
   * Experience preset (Disclosure Collector v1, M1 #1747). `collector` yields the
   * three-item nav (Collection, Package, Settings) for every client regardless of
   * firm-managed status. Optional so existing call sites keep compiling.
   */
  preset?: FeaturePreset;
}

/** The collector product's entire nav: three items, no "More" group. */
export const COLLECTOR_NAV_HREFS = ['/collection', '/export', '/settings'] as const;

/**
 * Decide the client's navigation shape.
 *
 * Compact ⇔ isFirmManaged (#1516). The pilot flag no longer gates the compact
 * nav — a firm-managed client on ANY route must get the consolidated nav, and
 * flag-load timing must never flash the legacy DIY sidebar at them.
 */
export function buildClientNav({
  isFirmManaged,
  t,
  preset,
}: BuildClientNavParams): ClientNav {
  if (preset === 'collector') {
    return {
      mode: 'compact',
      primary: [
        { label: t('sidebar.collection', 'Collection'), href: '/collection', iconKey: 'disclosure' },
        { label: t('sidebar.package', 'Package'), href: '/export', iconKey: 'export' },
        { label: t('sidebar.settings', 'Settings'), href: '/settings', iconKey: 'settings' },
      ],
      more: [],
    };
  }

  const useCompact = isFirmManaged;

  if (!useCompact) {
    return { mode: 'full' };
  }

  // Workflow-first primary items.
  // - "My Tasks" → /tasks, a focused view of the client's document requests /
  //   next actions (#1312) with a real empty state — distinct from Documents.
  // - "Documents" → /case-library, the document/disclosure hub under the HHD
  //   pilot (DisclosureStatusBadge lives there).
  const primary: ClientNavItem[] = [
    { label: t('sidebar.home', 'Home'), href: '/dashboard', iconKey: 'home' },
    { label: t('sidebar.myTasks', 'My Tasks'), href: '/tasks', iconKey: 'tasks' },
    {
      label: t('sidebar.documents', 'Documents'),
      href: '/case-library',
      iconKey: 'disclosure',
    },
    { label: t('sidebar.caseReadiness', 'Case Readiness'), href: '/case-readiness', iconKey: 'disclosure' },
    { label: t('sidebar.discovery', 'Discovery'), href: '/discovery', iconKey: 'discovery' },
    { label: t('sidebar.myTeam', 'My Team'), href: '/my-team', iconKey: 'team' },
  ];

  // Advanced DIY tools, tucked behind "More".
  const more: ClientNavItem[] = [
    { label: t('sidebar.financials', 'Financials'), href: '/financials', iconKey: 'financials' },
    { label: t('sidebar.strategy', 'Strategy'), href: '/strategy', iconKey: 'strategy' },
    {
      label: t('sidebar.caseIntelligence', 'Case Intelligence'),
      href: '/case-intelligence',
      iconKey: 'intelligence',
    },
    { label: t('sidebar.askSage', 'Ask Sage'), href: '/ask-sage', iconKey: 'askSage' },
    {
      label: t('sidebar.supportCalculator', 'Support Calculator'),
      href: '/support-calculator',
      iconKey: 'support',
    },
    { label: t('sidebar.notes', 'Notes'), href: '/notes', iconKey: 'notes' },
    { label: t('sidebar.export', 'Export'), href: '/export', iconKey: 'export' },
    { label: t('sidebar.help', 'Help'), href: '/guides', iconKey: 'help' },
    { label: t('sidebar.settings', 'Settings'), href: '/settings', iconKey: 'settings' },
  ];

  return {
    mode: 'compact',
    primary: primary.slice(0, MAX_PRIMARY_ITEMS),
    more,
  };
}
